import type { Layout } from '../../../data/types';
import type { Vec2 } from '../../../geo/types';
import { distancePointSegment } from '../../../geo/polygon';
import type { ReservedFootprint } from '../types';

function nearestVertex(ring: readonly Vec2[], p: Vec2): number {
  let best = 0;
  ring.forEach((q, i) => {
    if (
      Math.hypot(q[0] - p[0], q[1] - p[1]) <
      Math.hypot(ring[best]![0] - p[0], ring[best]![1] - p[1])
    )
      best = i;
  });
  return best;
}

function bankBeside(ring: readonly Vec2[], street: readonly Vec2[]): readonly Vec2[] {
  const first = nearestVertex(ring, street[0]!),
    last = nearestVertex(ring, street[street.length - 1]!);
  const path = (step: number): Vec2[] => {
    const out = [ring[first]!];
    for (
      let at = (first + step + ring.length) % ring.length;
      at !== last;
      at = (at + step + ring.length) % ring.length
    )
      out.push(ring[at]!);
    return [...out, ring[last]!];
  };
  const score = (points: readonly Vec2[]) =>
    points.reduce(
      (sum, p) =>
        sum +
        Math.min(
          ...street.slice(1).map((q, i) => distancePointSegment(p, { a: street[i]!, b: q }))
        ),
      0
    ) / points.length;
  const forward = path(1),
    reverse = path(-1);
  return score(forward) <= score(reverse) ? forward : reverse;
}

/** The blocks between the two retained streets and the water. No guessed city-wide rectangle. */
export function damrakBlockReservations(layout: Layout): readonly ReservedFootprint[] {
  const street = (id: string) => layout.streets.find((s) => s.id === id)?.centerline;
  const warmoes = street('street-warmoesstraat'),
    nieuwendijk = street('street-nieuwendijk');
  const quay = street('quay-damrak-west');
  const water = layout.water.areas.find((w) => w.id === 'damrak')?.polygon.outer;
  if (!warmoes || !nieuwendijk || !quay || !water) return [];
  const eastBank = bankBeside(water, warmoes);
  const northEnd = nearestVertex(nieuwendijk, quay[0]);
  const west: Vec2[] = [...quay, ...nieuwendijk.slice(0, northEnd + 1)];
  const east: Vec2[] = [...warmoes, ...[...eastBank].reverse()];
  return [
    { id: 'surveyed-damrak-west', polygon: { outer: west, holes: [] } },
    { id: 'surveyed-damrak-east', polygon: { outer: east, holes: [] } },
  ];
}
